import { useCallback } from 'react';
import { getActiveWorkspaceId, trpc } from '../lib/trpc';
import { useWorkspaceEvent } from './useWorkspacePresence';

const PAGE_SIZE = 30;

/**
 * The notification inbox, live.
 *
 * The backend has been broadcasting `NOTIFICATION_CREATED` over the workspace
 * Durable Object since notifications were built, with nothing listening. Consuming
 * it here means the bell updates the moment something happens rather than on the
 * next refetch — and, importantly, it works when push is off, denied, or
 * unsupported, which is most of the time for most users.
 *
 * The DO already filters that event down to the recipient's own sessions, so
 * arriving at all is proof it is ours.
 */
export function useNotifications() {
  const workspaceId = getActiveWorkspaceId();
  const meQuery = trpc.auth.me.useQuery();
  const utils = trpc.useUtils();

  const listQuery = trpc.notifications.list.useQuery(
    { cursor: 0, limit: PAGE_SIZE },
    { enabled: !!workspaceId }
  );
  const unreadCountQuery = trpc.notifications.unreadCount.useQuery(undefined, {
    enabled: !!workspaceId,
  });

  const refresh = useCallback(() => {
    void utils.notifications.list.invalidate();
    void utils.notifications.unreadCount.invalidate();
  }, [utils]);

  useWorkspaceEvent(workspaceId, meQuery.data?.uid ?? null, (event) => {
    if (event.type === 'NOTIFICATION_CREATED') refresh();
  });

  const markRead = trpc.notifications.markRead.useMutation({ onSuccess: refresh });
  const markAllRead = trpc.notifications.markAllRead.useMutation({ onSuccess: refresh });
  const dismiss = trpc.notifications.dismiss.useMutation({ onSuccess: refresh });

  return {
    notifications: listQuery.data?.items ?? [],
    totalCount: listQuery.data?.totalCount ?? 0,
    unreadCount: unreadCountQuery.data?.count ?? 0,
    loading: listQuery.isLoading,
    refresh,
    markRead,
    markAllRead,
    dismiss,
  };
}

export type NotificationItem = ReturnType<typeof useNotifications>['notifications'][number];
